import angular from "angular";
import notificationStreamModule from "../notificationStreamModule";

function gameUpdatesUtility(gameUpdatesService, urlService, $log, notificationStreamUtility, layoutLibraryService, gameUpdatesConstants) {
    "ngInject";

    var parseEpochMilliseconds = notificationStreamUtility.parseEpochMilliseconds,
        gameNameMaxLength = gameUpdatesConstants.gameNameMaxLength;

    function truncateGameName(gameName) {
        var truncated = gameName;

        if (gameName && gameName.length > gameNameMaxLength) {
            truncated = gameName.substr(0, gameNameMaxLength - 3) + "...";
        }

        return truncated;
    }

    function getGameUpdatesAsync(universeIds, gameUpdateModels, minEventDate) {
        var millisecondsInOneMinute = 60 * 1000;

        var promise = gameUpdatesService.multiGetGameUpdatesAsync(
            universeIds,
            minEventDate ? new Date(minEventDate.getTime() - millisecondsInOneMinute) : null
        ).then(
            function (gameUpdates) {
                var i, gameUpdate, model, createdOnEpoch;

                if (!gameUpdates || gameUpdates.length === 0) {
                    return null;
                }

                // Add to game update models.
                for (i = 0; i < gameUpdates.length; i++) {
                    gameUpdate = gameUpdates[i];
                    createdOnEpoch = parseEpochMilliseconds(gameUpdate.createdOn);

                    if (!gameUpdateModels.hasOwnProperty(gameUpdate.universeId)) {
                        gameUpdateModels[gameUpdate.universeId] = {
                            universeId: gameUpdate.universeId,
                            rootPlaceId: gameUpdate.rootPlaceId,
                            updateMessage: gameUpdate.content,
                            createdOn: createdOnEpoch,
                            createdOnKey: gameUpdate.createdOnKey,
                            gameName: gameUpdate.universeName,
                            truncatedGameName: truncateGameName(gameUpdate.universeName),
                            isPlayable: null,
                            gameIconUrl: null,
                            gameLinkUrl: gameUpdate.rootPlaceId ? urlService.getAbsoluteUrl("/games/" + gameUpdate.rootPlaceId + "?originatorType=GameUpdateNotification&originatorId=" + createdOnEpoch) : null,
                            gameLinkUrlForApp: gameUpdate.rootPlaceId ? "games/" + gameUpdate.rootPlaceId + "?originatorType=GameUpdateNotification&originatorId=" + createdOnEpoch : null
                        };
                    }
                    else {
                        model = gameUpdateModels[gameUpdate.universeId];
                        model.updateMessage = gameUpdate.content;
                        model.createdOn = createdOnEpoch;
                        model.createdOnKey = gameUpdate.createdOnKey;
                    }
                }

                return gameUpdates;
            }
        );

        return promise;
    }

    // Fill in game details and icons for models that still need them.
    function fillGameDetails(gameUpdateModels, useThumbnailsApi) {
        var newRootPlaceIds = [], newUniverseIds = [],
            universeId, model, iconsPromise = null, detailsPromise = null;

        // Helper to assign icons to game update models.
        // gameIcons parameter is a hash: universeId => icon URL.
        function assignIconsToModels(gameIcons) {
            if (!gameIcons) {
                return;
            }

            angular.forEach(gameIcons, function (gameIconUrl, universeId) {
                var model = gameUpdateModels[universeId];

                if (model) {
                    model.gameIconUrl = gameIconUrl;
                }
            });
        }

        for (universeId in gameUpdateModels) {
            if (!gameUpdateModels.hasOwnProperty(universeId)) {
                continue;
            }

            model = gameUpdateModels[universeId];

            if (model.isPlayable === null) {
                newRootPlaceIds.push(model.rootPlaceId);
            }

            if (model.gameIconUrl === null) {
                newUniverseIds.push(model.universeId);
            }
        }

        if (newRootPlaceIds.length === 0) {
            return;
        }

        // Start getting details and icons for new games.
        detailsPromise = gameUpdatesService.multiGetPlaceDetailsAsync(newRootPlaceIds).then(
            function (placeDetails) {
                if (!placeDetails) {
                    return;
                }

                angular.forEach(placeDetails, function (placeDetail, placeId) {
                    var model = gameUpdateModels[placeDetail.universeId];

                    if (model) {
                        model.isPlayable = placeDetail.isPlayable;
                    }
                });

                return placeDetails;
            }
        );

        if (newUniverseIds.length > 0) {
            iconsPromise = gameUpdatesService.multiGetGameIconsAsync(newUniverseIds).then(
                function (gameIcons) {
                    assignIconsToModels(gameIcons);
                }
            );
        }
    }

    function aggregateGameUpdateNotifications(gameUpdateNotifications, gameUpdateModels) {
        if (!gameUpdateNotifications || gameUpdateNotifications.length === 0) {
            return null;
        }

        var maxEventTimeStamp = null,
            minEventTimeStamp = null,
            maxEventDate = null,
            totalEventCount = 0,
            processed = {},
            aggregated = {};

        aggregated.id = gameUpdateNotifications[0].id;
        aggregated.notificationSourceType = gameUpdateNotifications[0].notificationSourceType;
        aggregated.metadataCollection = [];

        angular.forEach(gameUpdateNotifications, function (update) {
            var eventTimeStamp;

            // Find max event date.
            if (update.eventDate) {
                eventTimeStamp = parseEpochMilliseconds(update.eventDate);

                if (!maxEventTimeStamp || maxEventTimeStamp < eventTimeStamp) {
                    maxEventTimeStamp = eventTimeStamp;
                    maxEventDate = update.eventDate;
                }

                if (!minEventTimeStamp || minEventTimeStamp > eventTimeStamp) {
                    minEventTimeStamp = eventTimeStamp;
                }
            }

            // Aggregate meta data collection.
            angular.forEach(update.metadataCollection, function (metaData) {
                if (gameUpdateModels.hasOwnProperty(metaData.UniverseId) && !processed.hasOwnProperty(metaData.UniverseId)) {
                    aggregated.metadataCollection.push(metaData);
                    processed[metaData.UniverseId] = true;
                }
            });

            // Aggregate event count.
            if (update.eventCount) {
                totalEventCount = totalEventCount + update.eventCount;
            }
        });

        aggregated.eventDate = maxEventDate;
        aggregated.minEventDate = new Date(minEventTimeStamp);
        aggregated.eventCount = Math.max(totalEventCount, aggregated.metadataCollection.length);

        // Return null if there's no notification in the aggregated one.
        return aggregated.metadataCollection.length > 0 ? aggregated : null;
    }

    function formatAggregatedDisplayText(game1, game2, otherGameCount) {
        if (otherGameCount === 0) {
            return layoutLibraryService.gameUpdates.formatDisplayTextDouble(game1, game2);
        }

        return layoutLibraryService.gameUpdates.formatDisplayTextMultiple(game1, game2, otherGameCount);
    }

    function sortGameUpdatesByCreatedDate(gameUpdateModels, isAscending) {
        if (!gameUpdateModels) {
            return null;
        }

        var models = Object.keys(gameUpdateModels).map(function (k) {
            return gameUpdateModels[k];
        });

        models.sort(function (model1, model2) {
            return isAscending ? model1.createdOn - model2.createdOn : model2.createdOn - model1.createdOn;
        });

        return models;
    }

    return {
        formatAggregatedDisplayText: formatAggregatedDisplayText,
        getGameUpdatesAsync: getGameUpdatesAsync,
        fillGameDetails: fillGameDetails,
        aggregateGameUpdateNotifications: aggregateGameUpdateNotifications,
        sortGameUpdatesByCreatedDate: sortGameUpdatesByCreatedDate
    };
}

notificationStreamModule.factory("gameUpdatesUtility", gameUpdatesUtility);

export default gameUpdatesUtility;